/**
 * ImportLinesDataTable - Composant DataTable enterprise pour les lignes d'import
 *
 * Fonctionnalités:
 * - Pagination côté serveur (performance avec 25k+ lignes)
 * - Filtres par statut (erreur, warning, valide, etc.)
 * - Recherche textuelle dans les données
 * - Tri des colonnes clickable
 * - Correction inline des lignes en erreur
 * - Export CSV des erreurs
 * - Mise en surbrillance des erreurs
 *
 * Design System: Dashboard context (bg-white, text-gray-900)
 */

import { useState, useMemo, useCallback } from 'react';
import {
  Search,
  Filter,
  Download,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  AlertTriangle,
  CheckCircle,
  Clock,
  XCircle,
  SkipForward,
  Edit3,
  Save,
  X,
  RefreshCw,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import {
  useImportLines,
  useUpdateImportLine,
  useExportImportErrors,
  useRevalidateImport,
} from '../hooks/useImport';
import type { ImportLinesParams, ImportLineStatus, ImportLine, ValidationError } from '../types/import.types';
import { logger } from '@/utils/logger';

interface ImportLinesDataTableProps {
  importId: string;
  columns: string[];
  onLineUpdated?: () => void;
}

// Configuration des statuts avec icônes et couleurs
const STATUS_CONFIG: Record<ImportLineStatus, { icon: typeof CheckCircle; label: string; color: string; bgColor: string }> = {
  PENDING: { icon: Clock, label: 'En attente', color: 'text-gray-500', bgColor: 'bg-gray-50' },
  VALIDATED: { icon: CheckCircle, label: 'Valide', color: 'text-green-600', bgColor: 'bg-green-50' },
  ERROR: { icon: XCircle, label: 'Erreur', color: 'text-red-600', bgColor: 'bg-red-50' },
  WARNING: { icon: AlertTriangle, label: 'Attention', color: 'text-amber-600', bgColor: 'bg-amber-50' },
  SKIPPED: { icon: SkipForward, label: 'Ignorée', color: 'text-gray-400', bgColor: 'bg-gray-50' },
  PROCESSED: { icon: CheckCircle, label: 'Traitée', color: 'text-teal-600', bgColor: 'bg-teal-50' },
};

export function ImportLinesDataTable({ importId, columns, onLineUpdated }: ImportLinesDataTableProps) {
  // État local pour la pagination, filtres et tri
  const [params, setParams] = useState<ImportLinesParams>({
    page: 1,
    limit: 50,
    sortBy: 'rowNumber',
    sortOrder: 'asc',
  });
  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState<ImportLineStatus[]>([]);
  const [editingLine, setEditingLine] = useState<string | null>(null);
  const [editedData, setEditedData] = useState<Record<string, any>>({});

  // Hooks TanStack Query
  const { data, isLoading, isFetching, refetch } = useImportLines(importId, {
    ...params,
    search: searchValue || undefined,
    status: statusFilter.length > 0 ? statusFilter : undefined,
  });
  const updateLineMutation = useUpdateImportLine();
  const exportErrorsMutation = useExportImportErrors();
  const revalidateMutation = useRevalidateImport();

  // Colonnes FEC standard dans l'ordre officiel (Article A.47 A-1 du LPF)
  const FEC_COLUMN_ORDER = [
    'JournalCode', 'JournalLib', 'EcritureNum', 'EcritureDate',
    'CompteNum', 'CompteLib', 'CompAuxNum', 'CompAuxLib',
    'PieceRef', 'PieceDate', 'EcritureLib',
    'Debit', 'Credit', 'EcritureLet', 'DateLet', 'ValidDate',
    'Montantdevise', 'Idevise'
  ];

  // Colonnes à afficher - triées selon l'ordre FEC standard, puis le reste
  const displayColumns = useMemo(() => {
    // Trier les colonnes: d'abord celles du standard FEC (dans l'ordre), puis les autres
    const sortedColumns = [...columns].sort((a, b) => {
      const aIndex = FEC_COLUMN_ORDER.findIndex(col => col.toLowerCase() === a.toLowerCase());
      const bIndex = FEC_COLUMN_ORDER.findIndex(col => col.toLowerCase() === b.toLowerCase());

      // Si les deux sont dans le standard FEC, trier par leur ordre standard
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      // Si seulement a est dans le standard, a vient en premier
      if (aIndex !== -1) return -1;
      // Si seulement b est dans le standard, b vient en premier
      if (bIndex !== -1) return 1;
      // Sinon, garder l'ordre original
      return 0;
    });

    return sortedColumns; // Afficher toutes les colonnes
  }, [columns]);

  // Gestion de la recherche avec debounce
  const handleSearchChange = useCallback((value: string) => {
    setSearchValue(value);
    setParams((prev) => ({ ...prev, page: 1 })); // Reset à la page 1
  }, []);

  // Gestion du filtre par statut
  const toggleStatusFilter = useCallback((status: ImportLineStatus) => {
    setStatusFilter((prev) => {
      const newFilter = prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status];
      return newFilter;
    });
    setParams((prev) => ({ ...prev, page: 1 }));
  }, []);

  // Gestion du tri
  const handleSort = useCallback((column: string) => {
    setParams((prev) => {
      if (prev.sortBy === column) {
        return { ...prev, sortOrder: prev.sortOrder === 'asc' ? 'desc' : 'asc' };
      }
      return { ...prev, sortBy: column as ImportLinesParams['sortBy'], sortOrder: 'asc' };
    });
  }, []);

  // Gestion de la pagination
  const handlePageChange = useCallback((page: number) => {
    setParams((prev) => ({ ...prev, page }));
  }, []);

  const handleLimitChange = useCallback((limit: number) => {
    setParams((prev) => ({ ...prev, limit, page: 1 }));
  }, []);

  // Démarrer l'édition d'une ligne
  const startEditing = useCallback((line: ImportLine) => {
    setEditingLine(line.id);
    setEditedData(line.rawData);
  }, []);

  // Annuler l'édition
  const cancelEditing = useCallback(() => {
    setEditingLine(null);
    setEditedData({});
  }, []);

  // Sauvegarder les modifications
  const saveEditing = useCallback(async () => {
    if (!editingLine) return;

    try {
      await updateLineMutation.mutateAsync({
        importId,
        lineId: editingLine,
        data: { rawData: editedData },
      });
      setEditingLine(null);
      setEditedData({});
      onLineUpdated?.();
    } catch (error) {
      logger.error('Erreur lors de la sauvegarde:', error);
    }
  }, [editingLine, editedData, importId, updateLineMutation, onLineUpdated]);

  // Export des erreurs
  const handleExportErrors = useCallback(async () => {
    try {
      await exportErrorsMutation.mutateAsync(importId);
    } catch (error) {
      logger.error('Erreur lors de l\'export:', error);
    }
  }, [importId, exportErrorsMutation]);

  // Revalidation
  const handleRevalidate = useCallback(async () => {
    try {
      await revalidateMutation.mutateAsync(importId);
    } catch (error) {
      logger.error('Erreur lors de la revalidation:', error);
    }
  }, [importId, revalidateMutation]);

  // Render du badge de statut
  const renderStatusBadge = (status: ImportLineStatus) => {
    const config = STATUS_CONFIG[status];
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.bgColor} ${config.color}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  // Render des erreurs/warnings pour une ligne
  const renderErrors = (errors: ValidationError[] | null) => {
    if (!errors || errors.length === 0) return null;
    return (
      <div className="mt-1 space-y-1">
        {errors.map((error, idx) => (
          <div key={idx} className="text-xs text-red-600 flex items-start gap-1">
            <XCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
            <span><strong>{error.field}:</strong> {error.message}</span>
          </div>
        ))}
      </div>
    );
  };

  const renderWarnings = (warnings: ValidationError[] | null) => {
    if (!warnings || warnings.length === 0) return null;
    return (
      <div className="mt-1 space-y-1">
        {warnings.map((warning, idx) => (
          <div key={idx} className="text-xs text-amber-600 flex items-start gap-1">
            <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
            <span><strong>{warning.field}:</strong> {warning.message}</span>
          </div>
        ))}
      </div>
    );
  };

  // Render d'une cellule éditable
  const renderEditableCell = (line: ImportLine, column: string) => {
    const isEditing = editingLine === line.id;
    const value = isEditing ? editedData[column] : line.rawData[column];

    if (isEditing) {
      return (
        <input
          type="text"
          value={value ?? ''}
          onChange={(e) => setEditedData((prev) => ({ ...prev, [column]: e.target.value }))}
          className="w-full px-2 py-1 text-sm border border-teal-300 rounded focus:outline-none focus:ring-2 focus:ring-teal-500"
        />
      );
    }

    // Vérifier si cette cellule a une erreur
    const hasError = line.errors?.some((e) => e.field === column);
    const hasWarning = line.warnings?.some((w) => w.field === column);

    return (
      <span
        className={`${hasError ? 'text-red-600 font-medium' : ''} ${hasWarning ? 'text-amber-600' : ''}`}
        title={String(value ?? '')}
      >
        {value ?? '-'}
      </span>
    );
  };

  return (
    <div className="space-y-4">
      {/* Toolbar: Stats, Search, Filters, Actions */}
      <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
        {/* Stats rapides */}
        {data?.stats && (
          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-xs text-gray-700">
              Total: <strong>{data.total.toLocaleString('fr-FR')}</strong>
            </span>
            {data.stats.validated > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 rounded text-xs text-green-700">
                <CheckCircle className="w-3 h-3" /> {data.stats.validated.toLocaleString('fr-FR')}
              </span>
            )}
            {data.stats.error > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 rounded text-xs text-red-700">
                <XCircle className="w-3 h-3" /> {data.stats.error.toLocaleString('fr-FR')}
              </span>
            )}
            {data.stats.warning > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 rounded text-xs text-amber-700">
                <AlertTriangle className="w-3 h-3" /> {data.stats.warning.toLocaleString('fr-FR')}
              </span>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          {/* Recherche */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchValue}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent w-48"
            />
          </div>

          {/* Filtres statut */}
          <div className="flex items-center gap-1">
            <Filter className="w-4 h-4 text-gray-400" />
            {(['ERROR', 'WARNING', 'VALIDATED'] as ImportLineStatus[]).map((status) => {
              const config = STATUS_CONFIG[status];
              const isActive = statusFilter.includes(status);
              return (
                <button
                  key={status}
                  onClick={() => toggleStatusFilter(status)}
                  className={`px-2 py-1 text-xs rounded-full border transition-colors ${
                    isActive
                      ? `${config.bgColor} ${config.color} border-current`
                      : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {config.label}
                </button>
              );
            })}
          </div>

          {/* Boutons d'action */}
          <button
            onClick={handleRevalidate}
            disabled={revalidateMutation.isPending}
            className="inline-flex items-center gap-1 px-3 py-2 text-sm text-teal-700 bg-teal-50 border border-teal-200 rounded-lg hover:bg-teal-100 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${revalidateMutation.isPending ? 'animate-spin' : ''}`} />
            Revalider
          </button>

          <button
            onClick={handleExportErrors}
            disabled={exportErrorsMutation.isPending || !data?.stats?.error}
            className="inline-flex items-center gap-1 px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            Exporter erreurs
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-gray-700 font-medium w-16">
                  <button
                    onClick={() => handleSort('rowNumber')}
                    className="flex items-center gap-1 hover:text-teal-600"
                  >
                    #
                    {params.sortBy === 'rowNumber' ? (
                      params.sortOrder === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                    ) : (
                      <ArrowUpDown className="w-3 h-3 text-gray-400" />
                    )}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-gray-700 font-medium w-28">
                  <button
                    onClick={() => handleSort('status')}
                    className="flex items-center gap-1 hover:text-teal-600"
                  >
                    Statut
                    {params.sortBy === 'status' ? (
                      params.sortOrder === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                    ) : (
                      <ArrowUpDown className="w-3 h-3 text-gray-400" />
                    )}
                  </button>
                </th>
                {displayColumns.map((col) => (
                  <th key={col} className="px-4 py-3 text-left text-gray-700 font-medium truncate max-w-[150px]" title={col}>
                    {col}
                  </th>
                ))}
                <th className="px-4 py-3 text-right text-gray-700 font-medium w-24">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={displayColumns.length + 3} className="px-4 py-8 text-center text-gray-500">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2 text-teal-600" />
                    Chargement des données...
                  </td>
                </tr>
              ) : data?.lines.length === 0 ? (
                <tr>
                  <td colSpan={displayColumns.length + 3} className="px-4 py-8 text-center text-gray-500">
                    Aucune ligne trouvée
                  </td>
                </tr>
              ) : (
                data?.lines.map((line) => {
                  const isEditing = editingLine === line.id;
                  const statusConfig = STATUS_CONFIG[line.status];

                  return (
                    <tr
                      key={line.id}
                      className={`${statusConfig.bgColor} ${isEditing ? 'ring-2 ring-teal-500 ring-inset' : ''} hover:bg-opacity-75 transition-colors`}
                    >
                      <td className="px-4 py-3 text-gray-500 font-mono text-xs">{line.rowNumber}</td>
                      <td className="px-4 py-3">
                        {renderStatusBadge(line.status)}
                        {renderErrors(line.errors)}
                        {renderWarnings(line.warnings)}
                      </td>
                      {displayColumns.map((col) => (
                        <td key={col} className="px-4 py-3 truncate max-w-[150px]">
                          {renderEditableCell(line, col)}
                        </td>
                      ))}
                      <td className="px-4 py-3 text-right">
                        {isEditing ? (
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={saveEditing}
                              disabled={updateLineMutation.isPending}
                              className="p-1 text-green-600 hover:bg-green-100 rounded"
                              title="Sauvegarder"
                            >
                              <Save className="w-4 h-4" />
                            </button>
                            <button
                              onClick={cancelEditing}
                              className="p-1 text-gray-500 hover:bg-gray-100 rounded"
                              title="Annuler"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => startEditing(line)}
                            disabled={line.status === 'PROCESSED'}
                            className="p-1 text-gray-500 hover:text-teal-600 hover:bg-teal-50 rounded disabled:opacity-30"
                            title="Modifier"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {data && data.totalPages > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
          <div className="flex items-center gap-2 text-gray-600">
            <span>Afficher</span>
            <select
              value={params.limit}
              onChange={(e) => handleLimitChange(Number(e.target.value))}
              className="px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              {[25, 50, 100].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            <span>par page</span>
            {isFetching && !isLoading && (
              <RefreshCw className="w-4 h-4 animate-spin text-teal-600 ml-2" />
            )}
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={() => handlePageChange(1)}
              disabled={params.page === 1}
              className="p-2 text-gray-500 hover:text-teal-600 hover:bg-teal-50 rounded disabled:opacity-30 disabled:hover:bg-transparent"
            >
              <ChevronsLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => handlePageChange(params.page! - 1)}
              disabled={params.page === 1}
              className="p-2 text-gray-500 hover:text-teal-600 hover:bg-teal-50 rounded disabled:opacity-30 disabled:hover:bg-transparent"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            <span className="px-3 py-1 text-gray-700">
              Page <strong>{params.page}</strong> sur <strong>{data.totalPages}</strong>
            </span>

            <button
              onClick={() => handlePageChange(params.page! + 1)}
              disabled={params.page === data.totalPages}
              className="p-2 text-gray-500 hover:text-teal-600 hover:bg-teal-50 rounded disabled:opacity-30 disabled:hover:bg-transparent"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => handlePageChange(data.totalPages)}
              disabled={params.page === data.totalPages}
              className="p-2 text-gray-500 hover:text-teal-600 hover:bg-teal-50 rounded disabled:opacity-30 disabled:hover:bg-transparent"
            >
              <ChevronsRight className="w-4 h-4" />
            </button>
          </div>

          <div className="text-gray-500">
            {((params.page! - 1) * params.limit! + 1).toLocaleString('fr-FR')} - {Math.min(params.page! * params.limit!, data.total).toLocaleString('fr-FR')} sur {data.total.toLocaleString('fr-FR')}
          </div>
        </div>
      )}
    </div>
  );
}
